import { useState } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Card,
  CardContent,
  Badge,
  Link,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useAuth from '../../hooks/useAuth';
// components
import Avatar from '../../components/Avatar';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
import SvgIconStyle from '../../components/SvgIconStyle';
// util
import axios from '../../utils/axios';
import DIOText from '../../components/DIOText';

// ----------------------------------------------------------------------

const InfoStyle = styled('div')(({ theme }) => ({
  left: 0,
  right: 0,
  zIndex: 99,
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const IconStyle = styled(Iconify)(({ theme }) => ({
  width: 20,
  height: 20,
  flexShrink: 0,
  marginRight: theme.spacing(1),
  color: theme.palette.text.disabled,
}));

const WarningStyle = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

// ----------------------------------------------------------------------

export default function MatchingCrewProfile({ jobPost, candidate, isContractRunning=false }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const [isSubmit, setIsSubmit] = useState(false);

  const fileOpen = async (item) => {
    setIsSubmit(true);
    if (isContractRunning) {
      const companyId = user?.employee.company[0].id;
      const crewId = candidate?.id;
      const response = await axios.post(`/api/company/${companyId}/crews/${crewId}/files/${item.id}/url/`);
      window.open(response.data.url);
    } else {
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const candidateId = candidate?.id;
      const response = await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/files/${item.id}/url/`);
      window.open(response.data.url);
    }
    setIsSubmit(false);
  };

  const urlWithHttps = (url) => {
    if (!url) return ''
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`
    }
    return url
  }

  return (
    <Box>
      <InfoStyle>
        <Stack
          justifyContent={smUp ? "normal" : "center"}
          alignItems="center"
        >
        <Badge
          sx={{
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          badgeContent={
            <SvgIconStyle src={`/icons/ic_verified.svg`} sx={{ color: "info.main", mr: 2, mb: 3 }} />
          }
          invisible={!candidate?.dio_certified && !candidate?.instant_matched_at}
        >
          <Avatar
            src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${candidate?.crew_randomhash || '5'}.png`}
            alt={candidate?.crew_nickname}
            color={'default'}
            sx={{
              margin: 'auto',
              borderWidth: 2,
              borderStyle: 'solid',
              borderColor: 'common.white',
              width: { xs: 56, sm: 64 },
              height: { xs: 56, sm: 64 },
            }}
          />
        </Badge>
        </Stack>
        <Box
          sx={{
            ml: { xs: 0, md: 3 },
            mt: { xs: 1, md: 0 },
          }}
        >
          {/* 이름, 포지션 */}
          <Stack direction={{xs: "column", sm: "row"}} justifyContent={smUp ? "normal" : "center"} alignItems="center" spacing={{ xs: 0, sm: 1.5 }}>
            <Typography variant="h5">{candidate?.crew_nickname}</Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.secondary',
              }}
            >
              {candidate?.crew_position} • {candidate?.crew_years}년차
            </Typography>
          </Stack>

          {/* labels */}
          {candidate?.dio_certified && (
          <Stack sx={{ mt: 0.5 }} direction="row" alignItems="center" spacing={0.5} justifyContent={smUp ? "normal" : "center"}>
            <SvgIconStyle src={`/icons/ic_verified.svg`} sx={{ color: "info.main", width: 16, height: 16 }} />
            <Typography variant="caption" sx={{ color: "info.main", fontWeight: 'bold' }}>
              <DIOText sx={{ fontWeight: 'bold' }} />가 추천하는 크루입니다.
            </Typography>
          </Stack>
          )}
          {candidate?.contract_count > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 1.5, ...!smUp && { justifyContent: "center" } }}>
              <Label
                variant="outlined"
                sx={{ color: "grey.600", borderColor: "grey.600" }}
              >
                <DIOText /> 경험 {candidate?.contract_count}개월
              </Label>
              {candidate?.company_count > 0 && (
                <Label
                  variant="outlined"
                  sx={{ color: "grey.600", borderColor: "grey.600" }}
                >
                  {candidate?.company_count}개 회사
                </Label>
              )}
            </Stack>
          )}

          {/* 메인, 서브 역할 smUp */}
          <Stack direction={smUp ? "row" : "column"} sx={{ mt: 1 }} spacing={1}>
            {candidate?.crew_main_role && (
            <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="center">
              <Typography variant="subtitle2" whiteSpace="nowrap">메인</Typography>
              <Chip
                label={candidate?.crew_main_role}
                sx={{
                  backgroundColor: (theme) => theme.palette.grey[200],
                  color: "text.secondary",
                }}
              />
            </Stack>
            )}
            <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="center">
              <Typography variant="subtitle2" sx={{ pl: {xs : 0, sm: candidate?.crew_main_role ? 1 : 0} }} whiteSpace="nowrap">
                {candidate?.crew_main_role ? (candidate?.crew_sub_roles?.length > 0) ? '서브' : '' : '포지션'}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {candidate?.crew_sub_roles?.map((r, index) => (
                <Chip
                  sx={{
                    backgroundColor: (theme) => theme.palette.grey[200],
                    color: "text.secondary",
                  }}
                  label={r}
                  key={`sub-${index}`}
                />
              ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </InfoStyle>
      <Box sx={{ mt: { xs: 2, sm: 5 } }}>
        {candidate.crew_categories?.length > 0 && (
          <Stack sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/icon-medal-check-mono.svg`} sx={{ color: isContractRunning ? "secondary.main" : "text.disabled" }} />
              <Typography variant="h6">
                경력 카테고리
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ pl: 4, flexWrap: 'wrap', gap: 1 }}>
              {candidate.crew_categories?.map((item, index) => (
                <Chip label={item} key={index} />
              ))}
            </Stack>
          </Stack>
        )}

        {/* 크루 경력 정보 */}
        {candidate.crew_careers?.length > 0 && (
          <Stack sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic-job.svg`} sx={{ color: isContractRunning ? "secondary.main" : "text.disabled" }} />
              <Typography variant="h6">
                크루 경력 정보
              </Typography>
            </Stack>
            <Stack spacing={2} sx={{ pl: 4 }} divider={<Divider />}>
            {candidate.crew_careers?.map((career, index) => (
              <Stack key={index} spacing={1}>
                <Typography variant="subtitle1">
                  {career.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {career.period} {career.duration ? ` / ${career.duration}` : ''}
                </Typography>
                {career.description && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {career.description}
                  </Typography>
                )}
              </Stack>
            ))}
            </Stack>
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <SvgIconStyle src={`/icons/ic_person.svg`} sx={{ color: isContractRunning ? "secondary.main" : "text.disabled" }} />
          <Typography variant="h6">
            크루 소개
          </Typography>
        </Stack>
        <Typography sx={{ pl: 4, mt: 1, mb: 3, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {candidate.comment}
        </Typography>

        {(candidate?.portfolio?.files?.length > 0 || candidate?.portfolio?.urls?.length > 0) && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <SvgIconStyle src={`/icons/icon-clip-mono.svg`} sx={{ color: isContractRunning ? "secondary.main" : "text.disabled" }} />
              <Typography variant="h6">
                크루 포트폴리오
              </Typography>
            </Stack>

            <Box sx={{ pl: 4 }}>
            {candidate.portfolio?.urls?.map((u, index) => (
              <Stack direction="row" alignItems="center" sx={{ pl: 0.5 }} key={index}>
                <Link
                  href={urlWithHttps(u.url)}
                  underline="none"
                  color="text.secondary"
                  fontWeight="bold"
                  target="_blank"
                  variant="body2"
                >
                  {candidate?.crew_nickname} 포트폴리오 링크
                </Link>
              </Stack>
            ))}
            {candidate.portfolio?.files?.map((f, index) => (
              <LoadingButton
                key={index}
                sx={{ textTransform: 'none', color: 'text.secondary', ml: index === 0 ? 0 : 0.5, textDecoration: "underline" }}
                onClick={() => fileOpen(f)}
                loading={isSubmit}
              >
                {candidate?.crew_nickname} 포트폴리오
              </LoadingButton>
            ))}
            </Box>

            <WarningStyle>
              <Stack direction="row" alignItems="center" sx={{ pl: 2 }}>
                <IconStyle icon={"eva:alert-triangle-fill"} sx={{ color: 'warning.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                  {smUp && '크루의 포트폴리오가 '}외부에 유출되지 않게 주의해주세요.
                </Typography>
              </Stack>
            </WarningStyle>
          </>
        )}
      </Box>
    </Box>
  );
}
